import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { trackEvent } from '@/utils/analytics/useAnalytics'

import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react'

import {
  Flex as DarkFlex,
  DarkProvider,
  Tabs as DarkTabs,
  TabItem,
  tokenPrefix,
} from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectIncidentTraceLogDrawerState } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'

import { DeviceTraceLogsEmptyState as DarkDeviceTraceLogsEmptyState } from '../../dark/drawers/trace-log/DeviceTraceLogsEmptyState'
import { EventFiltersI } from '../../shared/types/traceLogTypes'
import { getPrimaryDeviceId } from '../../shared/utils/feedUtils'
import {
  DEVICES_TAB_EVENT,
  FACILITIES_TAB_EVENT,
  INCIDENT_TAB_EVENT,
  getIsTailgatingIncident,
  getMixpanelEventData,
  getTraceLogDefaultTabIndex,
} from '../../shared/utils/traceLogUtils'
import { DeviceTraceLogsEmptyState } from './DeviceTraceLogsEmptyState'
import { EventFeed } from './EventFeed'
import { TraceLogHeader } from './TraceLogHeader'

// The 112px here are the Header, Tab and padding height values
export const TRACE_LOG_PANEL_HEIGHT = 'calc(100vh - 135px)'

type TraceLogTabId = 'incident' | 'device' | 'facility'

const TAB_EVENT_BY_ID: Record<TraceLogTabId, string> = {
  incident: INCIDENT_TAB_EVENT,
  device: DEVICES_TAB_EVENT,
  facility: FACILITIES_TAB_EVENT,
}

export const TraceLogContent = ({ incident }: EventFiltersI) => {
  const { highlightedEvent, deviceId, shouldForceDeviceTab } = useAppSelector(
    selectIncidentTraceLogDrawerState
  )
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()

  // Show Incident tab for Tailgating incidents with associated events,
  // or when opened from a resolution EVENT_REF click. Not for the Reasoning
  // Trail device deep-link: it sets `highlightedEvent` for the Device-tab
  // highlight, but its event is device-scoped, so an Incident tab would be a
  // spurious (usually empty) panel.
  const isTailgatingIncident = getIsTailgatingIncident(
    incident,
    isIncidentTypeDefinition
  )
  const hasIncidentEvents =
    isTailgatingIncident && incident?.eventExternalIds?.length > 0
  const shouldShowIncidentTab =
    !shouldForceDeviceTab && (hasIncidentEvents || !!highlightedEvent)

  // Device the Device tab filters to. The Reasoning Trail deep-link supplies a
  // specific device; otherwise fall back to the incident's primary device.
  const deviceFilterId = deviceId ?? getPrimaryDeviceId(incident)

  const defaultIndex = getTraceLogDefaultTabIndex(
    shouldForceDeviceTab,
    shouldShowIncidentTab
  )

  // Controlled index: the drawer content stays mounted, so `defaultIndex` alone
  // won't switch tabs when a new deep-link updates Redux.
  const [tabIndex, setTabIndex] = useState(defaultIndex ?? 0)

  // Re-select the intended tab when the deep-link intent changes. `deviceId` is
  // a dep so a fresh link to a different device re-asserts the Device tab.
  useEffect(() => {
    const target = getTraceLogDefaultTabIndex(
      shouldForceDeviceTab,
      shouldShowIncidentTab
    )
    if (target !== undefined) {
      setTabIndex(target)
    }
  }, [shouldForceDeviceTab, shouldShowIncidentTab, deviceId])

  const handleMixPanelEvent = (tabName: string) => {
    trackEvent(
      `${BUTTON_PRESS} ${tabName} tab - trace logs`,
      getMixpanelEventData(incident)
    )
  }

  if (isRebrand) {
    const prefix = tokenPrefix(mode)
    const items: TabItem[] = [
      ...(shouldShowIncidentTab ? [{ id: 'incident', label: 'Incident' }] : []),
      { id: 'device', label: 'Device' },
      { id: 'facility', label: 'Facility' },
    ]
    // The numeric index keeps the deep-link logic above shared between both
    // branches; ordering matches the legacy TabList (Incident first when shown).
    const activeId = (items[tabIndex]?.id ?? 'device') as TraceLogTabId

    const handleTabChange = (id: string) => {
      const index = items.findIndex((item) => item.id === id)
      if (index >= 0) {
        setTabIndex(index)
      }
      // Guarded lookup: a tab id without a mapped event name must not track
      // a malformed "undefined tab" Mixpanel event.
      const tabEvent = TAB_EVENT_BY_ID[id as TraceLogTabId] as
        | string
        | undefined
      if (tabEvent) {
        handleMixPanelEvent(tabEvent)
      }
    }

    return (
      <DarkProvider>
        <DarkFlex direction='column' flex='1 1 0%' minH={0}>
          <TraceLogHeader />
          <DarkTabs
            activeId={activeId}
            colorScheme={mode}
            items={items}
            onChange={handleTabChange}
            tabListStyleOverrides={{
              borderBottomColor: `${prefix}border.subtle`,
              flexShrink: 0,
              gap: '8px',
              px: '24px',
            }}
            tabStyleOverrides={{
              flex: '1 0 0',
              fontSize: '14px',
              h: '40px',
              justifyContent: 'center',
              px: 0,
            }}
          />
          {activeId === 'incident' && (
            <EventFeed
              filters={{ deviceId: getPrimaryDeviceId(incident) }}
              incident={incident}
              showOnlyIncidentRelatedEvents={true}
            />
          )}
          {activeId === 'device' && (
            <EventFeed
              emptyState={
                <DarkDeviceTraceLogsEmptyState
                  onViewFacility={
                    incident?.facility?.id
                      ? () => handleTabChange('facility')
                      : undefined
                  }
                />
              }
              filters={{ deviceId: deviceFilterId }}
              incident={incident}
            />
          )}
          {activeId === 'facility' && (
            <EventFeed
              filters={{ facilityId: incident?.facility?.id }}
              incident={incident}
            />
          )}
        </DarkFlex>
      </DarkProvider>
    )
  }

  // Facility is the last tab, after Device and the optional Incident tab.
  const facilityTabIndex = shouldShowIncidentTab ? 2 : 1
  const handleViewFacility = () => {
    setTabIndex(facilityTabIndex)
    handleMixPanelEvent(FACILITIES_TAB_EVENT)
  }

  return (
    <Box>
      <TraceLogHeader />
      <Tabs
        borderColor='transparent'
        fontWeight='500'
        index={tabIndex}
        isFitted
        isLazy
        onChange={setTabIndex}
        w='100%'
      >
        <TabList>
          {shouldShowIncidentTab && (
            <Tab
              _selected={{
                color: 'components.tab.active',
                borderBottomColor: 'components.tab.active',
                opacity: 1,
              }}
              data-testid='incident-tab'
              onClick={() => handleMixPanelEvent(INCIDENT_TAB_EVENT)}
              opacity={0.4}
            >
              <Box
                alignItems='center'
                display='inline-flex'
                paddingBottom='8px'
                paddingTop='12px'
              >
                Incident
              </Box>
            </Tab>
          )}
          <Tab
            _selected={{
              color: 'components.tab.active',
              borderBottomColor: 'components.tab.active',
              opacity: 1,
            }}
            onClick={() => handleMixPanelEvent(DEVICES_TAB_EVENT)}
            opacity={0.4}
          >
            <Box
              alignItems='center'
              display='inline-flex'
              paddingBottom='8px'
              paddingTop='12px'
            >
              Device
            </Box>
          </Tab>
          <Tab
            _selected={{
              color: 'components.tab.active',
              borderBottomColor: 'components.tab.active',
              opacity: 1,
            }}
            onClick={() => handleMixPanelEvent(FACILITIES_TAB_EVENT)}
            opacity={0.4}
          >
            <Box
              alignItems='center'
              display='inline-flex'
              paddingBottom='8px'
              paddingTop='12px'
            >
              Facility
            </Box>
          </Tab>
        </TabList>
        <TabPanels px='0'>
          {shouldShowIncidentTab && (
            <TabPanel px='0' py='0px'>
              <EventFeed
                filters={{ deviceId: getPrimaryDeviceId(incident) }}
                incident={incident}
                showOnlyIncidentRelatedEvents={true}
              />
            </TabPanel>
          )}
          <TabPanel px='0' py='0px'>
            <EventFeed
              emptyState={
                <DeviceTraceLogsEmptyState
                  onViewFacility={
                    incident?.facility?.id ? handleViewFacility : undefined
                  }
                />
              }
              filters={{ deviceId: deviceFilterId }}
              incident={incident}
            />
          </TabPanel>
          <TabPanel px='0' py='0px'>
            <EventFeed
              filters={{ facilityId: incident?.facility?.id }}
              incident={incident}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}
